// @ts-ignore
import { useEffect, useRef } from 'react';
import { appActionTypes } from 'src/app/newActionTypes';
import { filtersInitialValues } from 'src/app/reducers/initialStates';
import { fetchFilterData } from 'src/app/resolvers/filtersResolver';
import { appAvailableFilters } from 'src/config';
import { DATA_FILTER_DIR } from 'src/constants';
import { useConfigSelectors, useFilterDataSelectors } from 'src/app/reducers';
import { DYNAMIC_FILTERS_GRAPHQL } from 'src/app/featureToggles';
import { useGetAllFilters } from 'src/app/gql/queries/filters/allFilters';
import { GetAllFiltersQuery } from 'src/app/gql/__generated__/hooks';
import { getCachedFilters, setCachedFilters } from 'src/app/functions/storage';
import { getDeployedProductId } from 'src/utils/generics';
import {
  IOperationsFilters,
  IReportsFilters,
  IAircraftAssignmentsFilters,
  IAirtrakFilters,
  IInfringementsFilters,
  INoiseEventsFilters,
  ICompliantsFilters,
  ISettingsFilters,
} from 'src/app/props';

// Operations filters loaded via GraphQL don't cover these - they either come
// from elsewhere (operationTags, see useOperationsFilter.ts) or don't yet
// have a GraphQL source (userEmails, changelogFields).
const OPERATIONS_FILTERS_NOT_IN_GRAPHQL: Pick<
  IOperationsFilters,
  'operationTags' | 'userEmails' | 'changelogFields'
> = {
  operationTags: [],
  userEmails: [],
  changelogFields: [],
};

// Complaints filters loaded via GraphQL don't cover this - it doesn't yet
// have a GraphQL source. These are the same two values
// data/filters/complaints/default.json supplies, and they're fixed rather
// than site data: responseRequired is a boolean on a complaint, and
// @complaints/reducers/constants.ts resolves the selection with
// `item.key === 'Yes'`. Defaulting to [] instead rendered a visible but
// empty "Response required" filter on any site listing it in
// availableCategoryList. Hiding the filter is that config's job, not ours.
const COMPLAINTS_FILTERS_NOT_IN_GRAPHQL: Pick<ICompliantsFilters, 'responseRequired'> = {
  responseRequired: ['Yes', 'No'],
};

// Settings filters loaded via GraphQL don't cover this - availableLanguages
// isn't actually consumed off this filter data anywhere (it's sourced from
// user/site config instead, see useGetUserLanguage), so this is kept only
// for parity with the static JSON shape.
const SETTINGS_FILTERS_NOT_IN_GRAPHQL: Pick<ISettingsFilters, 'availableLanguages'> = {
  availableLanguages: [],
};

// settings.daysOfWeek is consumed *positionally*, not by value:
// @settings/containers/LoadFactorsContainer maps each entry by its array
// index into getDayWithPosition, which hard-codes 0 -> 'S_____',
// 1 -> '_M_____' and so on. The static JSON file was Sunday-first, so the
// order was implicitly guaranteed; nothing guarantees it for a GraphQL
// response. Re-sort into the same canonical order so a server-side ordering
// change can't silently mislabel the Load Factors day columns.
const DAYS_OF_WEEK_ORDER = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const sortDaysOfWeek = (daysOfWeek: string[]): string[] => {
  // Anything unrecognised keeps its relative order at the end rather than
  // being dropped or sorted to the front (indexOf would return -1).
  const orderOf = (day: string): number => {
    const index = DAYS_OF_WEEK_ORDER.indexOf(String(day).toLowerCase());
    return index === -1 ? DAYS_OF_WEEK_ORDER.length : index;
  };

  return [...daysOfWeek].sort((a, b) => orderOf(a) - orderOf(b));
};

// Filter domains that have (or are being migrated to have) a GraphQL source -
// see useLoad<Domain>Filters below. When DYNAMIC_FILTERS_GRAPHQL is enabled,
// loadFilters skips fetching the static JSON file for these so it doesn't
// clobber the GraphQL-sourced data in the store.
const GRAPHQL_SOURCED_FILTER_DOMAINS = [
  'operations',
  'reports',
  'modelingAircraftAssignments',
  'airtrak',
  'infringements',
  'noiseEvents',
  'complaints',
  'settings',
] as const;

export const loadFilters = (dispatcher, config, dynamicFiltersEnabled = false): void => {
  const filters = (config?.globals?.data?.filters ?? {}) as Record<string, string | undefined>;

  const filterFileNameKeysAndSiteCodeValues = {
    infringements: filters.infringements ?? 'default',
    noiseEvents: filters.noiseEvents ?? 'default',
    operations: filters.operations ?? 'default',
    airtrak: filters.airtrak ?? 'default',
    complaints: filters.complaints ?? 'default',
    settings: filters.settings ?? 'default',
    modelingAircraftAssignments: filters.modelingAircraftAssignments ?? 'default',
    reports: filters.reports ?? 'default',
  };

  const isGraphqlSourcedDomain = (domain: string): boolean =>
    dynamicFiltersEnabled && (GRAPHQL_SOURCED_FILTER_DOMAINS as readonly string[]).includes(domain);

  // These domains are sourced from GraphQL (useLoad<Domain>Filters) in this
  // mode - omit them so the UPDATE_FILTERS dispatch below doesn't clobber
  // that data, and so their static JSON file isn't fetched at all.
  const appFilters = Object.fromEntries(
    Object.entries(filtersInitialValues).filter(([domain]) => !isGraphqlSourcedDomain(domain))
  ) as typeof filtersInitialValues;

  const promises: any[] = [];
  let filtersToLoad = 'Filters to load: \n';
  Object.entries(filterFileNameKeysAndSiteCodeValues)
    .filter(([path]) => !isGraphqlSourcedDomain(path))
    .forEach(([path, fileName]) => {
      if (
        fileName === 'default' ||
        appAvailableFilters.some(appFilter => {
          const filterString = `${DATA_FILTER_DIR}${path}/${fileName}`.toLowerCase();
          return appFilter.toLowerCase() === filterString;
        })
      ) {
        promises.push(fetchFilterData(path, fileName, appFilters));
        filtersToLoad += `\t ${path}: ${fileName}\n`;
      } else {
        promises.push(fetchFilterData(path, 'default', appFilters));
        filtersToLoad += `\t ${path}: using default ('${fileName}' specified, but not found)\n`;
      }
    });

  console.log(filtersToLoad);

  // UPDATE_FILTERS is what flips initialLoadCompleted to true, so it must
  // still fire even when every domain is GraphQL-sourced and there's
  // nothing left here to fetch - otherwise the app-level loading gate in
  // PageRouter never opens.
  if (promises.length > 0) {
    Promise.all(promises)
      .then(() => {
        dispatcher({ type: appActionTypes.UPDATE_FILTERS, payload: appFilters });
      })
      .catch(() => {
        dispatcher({
          type: appActionTypes.FILTERS_ERROR,
          payload: appFilters,
        });
      });
  } else {
    dispatcher({ type: appActionTypes.UPDATE_FILTERS, payload: appFilters });
  }
};

type AllFilters = NonNullable<GetAllFiltersQuery['filtersByDeployedProductId']>;

// infringements.acids is ~29k values on a large site, and the only page that
// lists it is Infringement Candidates, and only when the site's config shows
// an `acids` filter there. The Infringements page uses a free-text ACID box.
const isInfringementAcidsShown = (config?: {
  infringementsCandidates?: { filter?: { availableCategoryList?: string[] } };
}): boolean =>
  (config?.infringementsCandidates?.filter?.availableCategoryList ?? []).includes('acids');

const dispatchAllFilters = (dispatcher, allFilters: AllFilters): void => {
  const {
    operations,
    reports,
    modelingAircraftAssignments,
    airtrak,
    infringements,
    noiseEvents,
    complaints,
    settings,
  } = allFilters;

  // Every domain dispatches unconditionally, even when the server returned
  // null for it (each field is nullable, and a deployed product without a
  // given module legitimately returns null). Skipping the dispatch instead
  // would leave state.filters.<domain> as null, which both hangs the
  // app-level loading gate below forever and crashes that domain's page on
  // navigation (e.g. Object.entries(airtrakFilterData) in
  // @airtrak/containers/FilterContainer). An empty filter set degrades
  // gracefully; a null one does not.
  const mappedOperations: IOperationsFilters = {
    aircraftCategories: operations?.aircraftCategories ?? [],
    operatorCategories: operations?.operatorCategories ?? [],
    aircraftTypes: operations?.aircraftTypes ?? [],
    airportIds: operations?.airportIds ?? [],
    operationTypes: operations?.operationTypes ?? [],
    remoteAirportIds: operations?.remoteAirportIds ?? [],
    runwayNames: operations?.runwayNames ?? [],
    airlines: operations?.airlines ?? [],
    correlated: operations?.correlated ?? [],
    pathname: operations?.pathnames ?? [],
    ...OPERATIONS_FILTERS_NOT_IN_GRAPHQL,
  };
  dispatcher({ type: appActionTypes.UPDATE_OPERATIONS_FILTERS, payload: mappedOperations });

  const mappedReports: IReportsFilters = {
    dataType: reports?.dataType ?? [],
    reportFormat: reports?.reportFormat ?? [],
    reportInterval: reports?.reportInterval ?? [],
    templateName: reports?.templateName ?? [],
  };
  dispatcher({ type: appActionTypes.UPDATE_REPORTS_FILTERS, payload: mappedReports });

  const mappedModelingAircraftAssignments: IAircraftAssignmentsFilters = {
    engineModels: modelingAircraftAssignments?.engineModels ?? [],
  };
  dispatcher({
    type: appActionTypes.UPDATE_MODELING_AIRCRAFT_ASSIGNMENTS_FILTERS,
    payload: mappedModelingAircraftAssignments,
  });

  const mappedAirtrak: IAirtrakFilters = {
    emissions: airtrak?.emissions ?? [],
    aircraftTypes: airtrak?.aircraftTypes ?? [],
    engineModels: airtrak?.engineModels ?? [],
    airlines: airtrak?.airlines ?? [],
    regions: airtrak?.regions ?? [],
    phases: airtrak?.phases ?? [],
    ltoPhases: airtrak?.ltoPhases ?? [],
    outBoundPhases: airtrak?.outBoundPhases ?? [],
    inBoundPhases: airtrak?.inBoundPhases ?? [],
  };
  dispatcher({ type: appActionTypes.UPDATE_AIRTRAK_FILTERS, payload: mappedAirtrak });

  const mappedInfringements: IInfringementsFilters = {
    acids: infringements?.acids ?? [],
    aircraftCategories: infringements?.aircraftCategories ?? [],
    aircraftTypes: infringements?.aircraftTypes ?? [],
    airlines: infringements?.airlines ?? [],
    candidates: infringements?.candidates ?? [],
    infringementTypes: infringements?.infringementTypes ?? [],
    ruleNames: infringements?.ruleNames ?? [],
    severities: infringements?.severities ?? [],
    statuses: infringements?.statuses ?? [],
    vectored: infringements?.vectored ?? [],
  };
  dispatcher({ type: appActionTypes.UPDATE_INFRINGEMENTS_FILTERS, payload: mappedInfringements });

  // classificationGroups is a structured object at runtime, not the
  // `string` the generated types claim. That isn't a stale schema.json -
  // both the dev and QA APIs introspect NoiseEventsFilter.classificationGroups
  // as a String scalar while returning an object, so the cast below is
  // load-bearing until the API's own schema is corrected.
  //
  // Its keys arrive lower-camel-cased (aircraft, community, weather,
  // equipment, notSet) while `classifications` and every downstream
  // consumer (e.g. filterReducer.tsx's
  // `state.classificationGroups[classification]` lookup) use the
  // capitalised form. Capitalise whatever keys the server actually sent
  // rather than reading six hard-coded ones, so a classification added
  // server-side maps through instead of silently becoming an empty group.
  const rawClassificationGroups =
    (noiseEvents?.classificationGroups as unknown as Record<string, string[] | undefined>) ?? {};

  const classificationGroups = Object.entries(rawClassificationGroups).reduce(
    (groups, [group, enumValues]) => {
      groups[`${group.charAt(0).toUpperCase()}${group.slice(1)}`] = enumValues ?? [];
      return groups;
    },
    // The keys INoiseEventsFilters declares are seeded empty so consumers
    // reading them directly still get an array when the server omits them.
    {
      Unknown: [],
      Aircraft: [],
      Community: [],
      Weather: [],
      Equipment: [],
      NotSet: [],
    } as INoiseEventsFilters['classificationGroups']
  );

  const mappedNoiseEvents: INoiseEventsFilters = {
    aircraftCategories: noiseEvents?.aircraftCategories ?? [],
    operatorCategories: noiseEvents?.operatorCategories ?? [],
    classifications: noiseEvents?.classifications ?? [],
    locationNames: noiseEvents?.locationNames ?? [],
    classificationGroups,
  };
  dispatcher({ type: appActionTypes.UPDATE_NOISE_EVENTS_FILTERS, payload: mappedNoiseEvents });

  const mappedComplaints: ICompliantsFilters = {
    disturbanceTypes: complaints?.disturbanceTypes ?? [],
    hasOperation: complaints?.hasOperation ?? [],
    statuses: complaints?.statuses ?? [],
    ...COMPLAINTS_FILTERS_NOT_IN_GRAPHQL,
  };
  dispatcher({ type: appActionTypes.UPDATE_COMPLAINTS_FILTERS, payload: mappedComplaints });

  const mappedSettings: ISettingsFilters = {
    aircraftTypes: settings?.aircraftTypes ?? [],
    airlines: settings?.airlines ?? [],
    daysOfWeek: sortDaysOfWeek(settings?.daysOfWeek ?? []),
    destination: settings?.destination ?? [],
    origin: settings?.origin ?? [],
    ...SETTINGS_FILTERS_NOT_IN_GRAPHQL,
  };
  dispatcher({ type: appActionTypes.UPDATE_SETTINGS_FILTERS, payload: mappedSettings });
};

// Loads every GraphQL-sourced filter domain (Operations, Reports, Modeling
// Aircraft Assignments, Airtrak, Infringements, Noise Events, Complaints,
// Settings) in a single request instead of the static JSON files, gated
// behind the DYNAMIC_FILTERS_GRAPHQL site-config feature toggle. When
// disabled, this is a no-op and `loadFilters` continues to source every
// domain from JSON as before.
export const useLoadAllFilters = (dispatcher): { loading: boolean } => {
  const configSelectors = useConfigSelectors();
  const filterDataSelectors = useFilterDataSelectors();
  const configLoaded = configSelectors.hasCompletedInitialLoad();
  const useGraphqlFilters = configLoaded
    ? configSelectors.isFeatureAvailable(DYNAMIC_FILTERS_GRAPHQL)
    : false;

  const includeInfringementAcids = configLoaded
    ? isInfringementAcidsShown(configSelectors.getConfig())
    : false;

  const { allFilters, error } = useGetAllFilters(
    !configLoaded || !useGraphqlFilters,
    includeInfringementAcids
  );
  const hasFallenBackToJson = useRef(false);
  // JSON of whatever was last dispatched (cached or network), or null if
  // nothing has been yet.
  const lastDispatched = useRef<string | null>(null);
  const siteId = getDeployedProductId();

  // Show the last saved result straight away, so a repeat visit doesn't sit
  // on PageSkeleton while the (slow, on a large site) request runs. The
  // network result below then replaces it. Pages that copy their options once
  // when they open keep the cached ones until the user navigates.
  useEffect(() => {
    if (!useGraphqlFilters || !siteId || lastDispatched.current !== null) {
      return;
    }

    const cached = getCachedFilters<AllFilters>(siteId);
    if (cached) {
      lastDispatched.current = JSON.stringify(cached);
      dispatchAllFilters(dispatcher, cached);
    }
  }, [useGraphqlFilters]);

  useEffect(() => {
    if (!useGraphqlFilters || !allFilters) {
      return;
    }

    // Only a complete result is saved - a partial one would show empty
    // domains on the next load.
    if (!error && siteId) {
      setCachedFilters(siteId, allFilters);
    }

    // With cached values already on screen, keep them rather than replace
    // good domains with the empty ones a partial error leaves behind.
    if (error && lastDispatched.current !== null) {
      return;
    }

    // Filter lists rarely change, so the network result usually matches the
    // cache. Skip the second dispatch then, rather than re-render every
    // consumer for nothing.
    const serialised = JSON.stringify(allFilters);
    if (serialised === lastDispatched.current) {
      return;
    }
    lastDispatched.current = serialised;
    dispatchAllFilters(dispatcher, allFilters);
  }, [allFilters, useGraphqlFilters]);

  // When the query fails outright (network error, auth expiry - no usable
  // data at all), fall back to the static JSON files. They still ship in
  // public/data/filters/, so this keeps the toggle fail-soft instead of
  // leaving every domain empty with nothing surfaced to the user. A partial
  // error is deliberately not handled here: the dispatches above already
  // populate every slot, so re-running the JSON load would only clobber the
  // domains that did resolve.
  useEffect(() => {
    if (!useGraphqlFilters || !error || allFilters || hasFallenBackToJson.current) {
      return;
    }
    // Cached values are already on screen - better than the static JSON.
    if (lastDispatched.current !== null) {
      return;
    }

    hasFallenBackToJson.current = true;
    console.error(
      'Failed to load filters via GraphQL - falling back to the static JSON filter files'
    );
    loadFilters(dispatcher, configSelectors.getConfig(), false);
  }, [error, allFilters, useGraphqlFilters]);

  // Apollo's `data` resolves a render before the dispatches above (fired
  // from the effect) are actually applied to the store. Gating on
  // `allFilters` directly would open the app-level loading gate for that one
  // render while the relevant state.filters.<domain> slots are still null,
  // crashing anything downstream that reads them. Gate on the store values
  // themselves so the two can never go out of sync.
  const allFilterDataLoaded =
    filterDataSelectors.getOperationsFilterData() != null &&
    filterDataSelectors.getReportsFilterData() != null &&
    filterDataSelectors.getModelingAircraftAssignmentsData() != null &&
    filterDataSelectors.getAirtrakFilterData() != null &&
    filterDataSelectors.getInfringementFilterData() != null &&
    filterDataSelectors.getNoiseEventsFilterData() != null &&
    filterDataSelectors.getComplaintsFilterData() != null &&
    filterDataSelectors.getSettingsFiltersData() != null;

  // Deliberately does NOT consult `error`. Doing so opened the gate on the
  // very render an error arrived - before the effects above had dispatched
  // anything - so PageRouter rendered every listing page while the filter
  // slots were still null, and each one dereferenced null immediately. The
  // JSON fallback above is what resolves an error now. hasAccessFailed() is
  // the last-resort release valve: if that fallback fetch also fails,
  // FILTERS_ERROR fires and the gate opens rather than hanging forever.
  return {
    loading:
      useGraphqlFilters && !allFilterDataLoaded && !filterDataSelectors.hasAccessFailed(),
  };
};
